import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { toast } from "react-hot-toast";
import {
  useRequestsApi,
  type RequestResponse,
} from "../../../hooks/useRequestsApi";
import PendingRequests from "./PendingRequests";
import ExplanationDetailsModal from "./ExplanationDetailsModal";
import AssignTeacherModal from "./AssignTeacherModal";
import RejectRequestModal from "./RejectRequestModal";

const AdminExplanations: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = React.useState("");
  const [allRequests, setAllRequests] = React.useState<RequestResponse[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = React.useState(false);
  const [showAssignModal, setShowAssignModal] = React.useState(false);
  const [showRejectModal, setShowRejectModal] = React.useState(false);
  const [selectedRequest, setSelectedRequest] =
    React.useState<RequestResponse | null>(null);

  // Utiliser le nouveau hook pour l'API
  const { getAllRequests, validateRequest, rejectRequest } = useRequestsApi();

  // Charger les données admin
  const fetchAdminData = React.useCallback(async () => {
    try {
      setLoading(true);
      const requestsResponse = await getAllRequests();

      if (
        requestsResponse.status === 200 &&
        Array.isArray(requestsResponse.body)
      ) {
        setError(null);
        setAllRequests(requestsResponse.body);
      } else {
        setError("Erreur lors de la récupération des demandes.");
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
      setError("Une erreur s'est produite lors de la connexion au serveur.");
    } finally {
      setLoading(false);
    }
  }, [getAllRequests]);

  React.useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setSelectedStatus(event.target.value);
  };

  // Ouvrir la modale de détails
  const handleViewDetails = (request: RequestResponse) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  // Ouvrir la modale d'assignation
  const handleAssignTeacher = (request: RequestResponse) => {
    setSelectedRequest(request);
    setShowAssignModal(true);
  };

  // Ouvrir la modale de rejet
  const handleRejectRequest = (request: RequestResponse) => {
    setSelectedRequest(request);
    setShowRejectModal(true);
  };

  // Confirmer l'assignation d'un professeur
  const handleAssignTeacherConfirm = async (teacherId: string) => {
    if (!selectedRequest) return;

    try {
      const response = await validateRequest(selectedRequest._id, {
        teacherId,
      });
      console.log("📨 Réponse validateRequest:", response);

      // Vérifier si la réponse est un succès (200-299)
      if (response.status >= 200 && response.status < 300) {
        // Vérifier si le body contient un message d'erreur
        if (
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body &&
          !("_id" in response.body)
        ) {
          toast.error(
            (response.body.message as string) || "Erreur lors de l'assignation",
          );
          return;
        }

        // Succès
        toast.success(
          "Professeur assigné avec succès ! Il sera notifié par email.",
        );
        setShowAssignModal(false);
        setSelectedRequest(null);
        fetchAdminData(); // Recharger les données
      } else {
        const errorMessage =
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body
            ? (response.body.message as string)
            : "Erreur lors de l'assignation du professeur";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("❌ Exception:", error);
      toast.error(
        error?.message || "Erreur lors de l'assignation du professeur",
      );
    }
  };

  // Confirmer le rejet d'une demande
  const handleRejectRequestConfirm = async (rejectionReason: string) => {
    if (!selectedRequest) return;

    try {
      const response = await rejectRequest(selectedRequest._id, {
        rejectionReason,
      });
      console.log("📨 Réponse rejectRequest:", response);

      // Vérifier si la réponse est un succès (200-299)
      if (response.status >= 200 && response.status < 300) {
        // Vérifier si le body contient un message d'erreur
        if (
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body &&
          !("_id" in response.body)
        ) {
          toast.error(
            (response.body.message as string) || "Erreur lors du rejet",
          );
          return;
        }

        // Succès
        toast.success(
          "Demande rejetée avec succès ! L'élève sera notifié par email.",
        );

        // Recharger les données
        await fetchAdminData();

        // Fermer la modale
        setShowRejectModal(false);
        setSelectedRequest(null);
      } else {
        const errorMessage =
          response.body &&
          typeof response.body === "object" &&
          "message" in response.body
            ? (response.body.message as string)
            : "Erreur lors du rejet de la demande";
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("❌ Exception:", error);
      toast.error(error?.message || "Erreur lors du rejet de la demande");
    }
  };

  const getFilteredRequests = () => {
    if (!allRequests) return [];

    let filteredRequests = allRequests;

    // Filtrer par statut spécifique (si sélectionné)
    if (selectedStatus) {
      filteredRequests = filteredRequests.filter(
        (r) => r.status === selectedStatus,
      );
    }

    return filteredRequests.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          maxWidth: "full",
          margin: "auto",
          bgcolor: "background.paper",
          p: { xs: 1, sm: 2 },
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
            Administration des explications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gérez toutes les demandes d'explications
          </Typography>
        </Box>

        {/* Filtre de statut uniquement */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            mb: 2,
          }}
        >
          <FormControl
            variant="standard"
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              "& .MuiInput-root": {
                fontSize: { xs: "0.875rem", sm: "1rem" },
              },
            }}
          >
            <Select
              value={selectedStatus}
              onChange={handleStatusChange}
              displayEmpty
              sx={{
                fontSize: { xs: "0.875rem", sm: "1rem" },
                "& .MuiSelect-select": {
                  padding: { xs: "8px 12px", sm: "12px 16px" },
                },
              }}
            >
              <MenuItem
                value=""
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Tous les statuts
              </MenuItem>
              <MenuItem
                value="pending"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                En attente
              </MenuItem>
              <MenuItem
                value="validated"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Validée
              </MenuItem>
              <MenuItem
                value="slots_proposed"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Créneaux proposés
              </MenuItem>
              <MenuItem
                value="student_confirmed"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Créneau confirmé
              </MenuItem>
              <MenuItem
                value="meeting_scheduled"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Réunion programmée
              </MenuItem>
              <MenuItem
                value="scheduled"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Programmée
              </MenuItem>
              <MenuItem
                value="completed"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Terminée
              </MenuItem>
              <MenuItem
                value="rejected"
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Rejetée
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />
        {/* Liste des demandes filtrées */}
        <div className="space-y-2">
          {getFilteredRequests().length === 0 ? (
            <p className="text-gray-500 text-center text-sm">
              Aucune demande trouvée.
            </p>
          ) : (
            <PendingRequests
              requests={getFilteredRequests()}
              onViewDetails={handleViewDetails}
              onAssignTeacher={handleAssignTeacher}
              onRejectRequest={handleRejectRequest}
            />
          )}
        </div>
      </Box>

      {/* Modales */}
      {showDetailsModal && selectedRequest && (
        <ExplanationDetailsModal
          open={showDetailsModal}
          request={selectedRequest}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedRequest(null);
          }}
        />
      )}

      {showAssignModal && selectedRequest && (
        <AssignTeacherModal
          open={showAssignModal}
          request={selectedRequest}
          onAssign={handleAssignTeacherConfirm}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedRequest(null);
          }}
        />
      )}

      {showRejectModal && selectedRequest && (
        <RejectRequestModal
          open={showRejectModal}
          request={selectedRequest}
          onReject={handleRejectRequestConfirm}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedRequest(null);
          }}
        />
      )}
    </>
  );
};

export default AdminExplanations;
